import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Avatar, Box, Button, Chip, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api/api";

const staticTeamImages = [
  { tokens: ["mafdy", "mafds", "farouk", "garas"], image: "/team-mafdy-farouk.jpg" },
  { tokens: ["mohamed", "abdulsattar"], image: "/team-mohamed-abdulsattar.jpg" },
  { tokens: ["miriam", "dirnberger"], image: "/team-miriam-dirnberger.jpg" },
  { tokens: ["sara", "elrefaey"], image: "/team-sara-elrefaey.jpg" },
  { tokens: ["erik", "lutz"], image: "/team-erik-lutz.jpeg" },
  { tokens: ["hoda", "mostafa"], image: "/team-hoda-mostafa.jpg" },
  { tokens: ["fatema", "alzahraa"], image: "/team-fatema-alzahraa.JPG" },
  { tokens: ["yara", "ahmed"], image: "/team-yara-ahmed.JPG" },
];

const labels = {
  en: {
    title: "Events",
    subtitle: "Live sessions, workshops, and community events with the ALGermanist team.",
    empty: "No events are available right now.",
    loading: "Loading events...",
    hostedBy: "Hosted by",
    date: "Event date",
    join: "Join Zoom",
    soon: "Zoom link coming soon",
    dateSoon: "Date and time will be announced soon",
    hostSoon: "Presenter will be announced soon",
    accessSoon: "Online access details will be published soon",
    comingSoon: "Coming soon",
    scheduled: "Scheduled",
    live: "Live now",
    cancelled: "Cancelled",
    finished: "Finished",
    unavailable: "Event details are currently unavailable.",
  },
  de: {
    title: "Events",
    subtitle: "Live-Sessions, Workshops und Community-Events mit dem ALGermanist Team.",
    empty: "Aktuell sind keine Events verfuegbar.",
    loading: "Events werden geladen...",
    hostedBy: "Durchgefuehrt von",
    date: "Eventdatum",
    join: "Zoom oeffnen",
    soon: "Zoom-Link folgt bald",
    dateSoon: "Datum und Uhrzeit werden bald bekannt gegeben",
    hostSoon: "Die Ansprechperson wird bald bekannt gegeben",
    accessSoon: "Die Online-Zugangsdaten werden bald veröffentlicht",
    comingSoon: "Demnächst",
    scheduled: "Geplant",
    live: "Jetzt live",
    cancelled: "Abgesagt",
    finished: "Beendet",
    unavailable: "Eventdetails sind aktuell nicht verfuegbar.",
  },
  ar: {
    title: "الفعاليات",
    subtitle: "جلسات مباشرة وورش عمل وفعاليات مع فريق ALGermanist.",
    empty: "لا توجد فعاليات متاحة الآن.",
    loading: "جاري تحميل الفعاليات...",
    hostedBy: "المسؤول عن الفعالية",
    date: "تاريخ الفعالية",
    join: "فتح Zoom",
    soon: "رابط Zoom قريبًا",
    dateSoon: "سيتم الإعلان عن التاريخ والوقت قريبًا",
    hostSoon: "سيتم الإعلان عن مقدم الفعالية قريبًا",
    accessSoon: "سيتم نشر بيانات الدخول عبر الإنترنت قريبًا",
    comingSoon: "قريبًا",
    scheduled: "مجدولة",
    live: "مباشر الآن",
    cancelled: "ملغاة",
    finished: "انتهت",
    unavailable: "تفاصيل الفعالية غير متاحة حاليًا.",
  },
};

const localeMap = {
  en: "en-US",
  de: "de-DE",
  ar: "ar-EG",
};

const getTeacherImageSrc = (profilePicture, teacherName) => {
  if (profilePicture) {
    if (profilePicture.startsWith("http")) return profilePicture;
    if (profilePicture.startsWith("/api/")) return `${process.env.REACT_APP_API_URL}${profilePicture}`;
    return profilePicture;
  }

  const normalizedName = (teacherName || "").toLowerCase();
  const match = staticTeamImages.find((entry) =>
    entry.tokens.some((token) => normalizedName.includes(token))
  );
  return match?.image || "/team-banner.png";
};

const EventsPage = () => {
  const { i18n } = useTranslation();
  const language = i18n.language?.split("-")[0] || "en";
  const lang = labels[language] ? language : "en";
  const isArabic = lang === "ar";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadEvents = async () => {
      try {
        const response = await api.get("/events");
        if (mounted) {
          setEvents(Array.isArray(response.data) ? response.data : []);
          setError("");
        }
      } catch (err) {
        if (mounted) setError(labels[lang].unavailable);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadEvents();
    return () => {
      mounted = false;
    };
  }, [lang]);

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat(localeMap[lang], {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [lang]
  );

  const pickText = (event, base) => {
    if (lang === "ar") return event[`${base}Ar`] || event[`${base}En`] || event[`${base}De`] || "";
    if (lang === "de") return event[`${base}De`] || event[`${base}En`] || event[`${base}Ar`] || "";
    return event[`${base}En`] || event[`${base}De`] || event[`${base}Ar`] || "";
  };

  const teacherSpecialization = (event) => {
    if (lang === "ar") {
      return event.assignedTeacherSpecializationAr || event.assignedTeacherSpecialization || event.assignedTeacherSpecializationDe;
    }
    if (lang === "de") {
      return event.assignedTeacherSpecializationDe || event.assignedTeacherSpecialization || event.assignedTeacherSpecializationAr;
    }
    return event.assignedTeacherSpecialization || event.assignedTeacherSpecializationDe || event.assignedTeacherSpecializationAr;
  };

  return (
    <Box sx={{ direction: isArabic ? "rtl" : "ltr", bgcolor: "#f6f8f8", minHeight: "100vh" }}>
      <Box
        sx={{
          minHeight: { xs: 360, md: 440 },
          display: "flex",
          alignItems: "flex-end",
          color: "#fff",
          backgroundImage:
            "linear-gradient(90deg, rgba(0,0,0,0.72), rgba(0,0,0,0.35)), url('/service-events.png')",
          backgroundSize: "cover",
          backgroundPosition: "center 28%",
          px: { xs: 3, sm: 5, md: 10 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 760, textAlign: isArabic ? "right" : "left" }}>
          <Chip
            icon={<EventAvailableIcon />}
            label={labels[lang].title}
            sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.92)", color: "#172124", fontWeight: 700 }}
          />
          <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: "2.25rem", md: "4rem" } }}>
            {labels[lang].title}
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, maxWidth: 680, lineHeight: 1.65 }}>
            {labels[lang].subtitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4, md: 10 }, py: { xs: 4, md: 7 } }}>
        {loading && (
          <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <CircularProgress size={28} />
            <Typography>{labels[lang].loading}</Typography>
          </Box>
        )}

        {!loading && (error || events.length === 0) && (
          <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
            <Typography>{error || labels[lang].empty}</Typography>
          </Paper>
        )}

        {!loading && events.length > 0 && (
          <Grid container spacing={3}>
            {events.map((event) => {
              const title = pickText(event, "title") || labels[lang].title;
              const description = pickText(event, "description");
              const teacherName = [event.assignedTeacherFirstName, event.assignedTeacherLastName]
                .filter(Boolean)
                .join(" ");
              const imageSrc = getTeacherImageSrc(event.assignedTeacherProfilePicture, teacherName);
              const eventDate = event.eventDateTime ? formatter.format(new Date(event.eventDateTime)) : labels[lang].dateSoon;
              const canJoin = Boolean(event.joinAvailable && event.zoomLink);
              const statusLabel = event.status === "CANCELLED" ? labels[lang].cancelled
                : event.finished ? labels[lang].finished
                : event.status === "LIVE" ? labels[lang].live
                : event.status === "SCHEDULED" ? labels[lang].scheduled
                : labels[lang].comingSoon;

              return (
                <Grid item xs={12} md={6} key={event.id}>
                  <Paper
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #dfe8e8",
                      boxShadow: "0 14px 34px rgba(20, 40, 45, 0.08)",
                    }}
                  >
                    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                      <Chip
                        label={statusLabel}
                        size="small"
                        sx={{ mb: 1.5, mr: isArabic ? 0 : 1, ml: isArabic ? 1 : 0, fontWeight: 800 }}
                      />
                      <Chip
                        icon={<AccessTimeIcon />}
                        label={`${labels[lang].date}: ${eventDate}`}
                        color={event.finished ? "default" : "primary"}
                        sx={{ mb: 2, fontWeight: 700 }}
                      />
                      <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5, color: "#132124" }}>
                        {title}
                      </Typography>
                      <Typography sx={{ color: "#405054", lineHeight: 1.8, whiteSpace: "pre-line", mb: 3 }}>
                        {description || labels[lang].unavailable}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "#eef4f4",
                          mb: 3,
                        }}
                      >
                        <Avatar src={imageSrc} alt={teacherName} sx={{ width: 66, height: 66 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#667477", fontWeight: 700 }}>
                            {labels[lang].hostedBy}
                          </Typography>
                          <Typography fontWeight={800}>{teacherName || labels[lang].hostSoon}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {teacherSpecialization(event) || event.assignedTeacherJob || ""}
                          </Typography>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        startIcon={!isArabic ? <VideocamIcon /> : null}
                        endIcon={isArabic ? <VideocamIcon /> : null}
                        href={canJoin ? event.zoomLink : undefined}
                        target={canJoin ? "_blank" : undefined}
                        rel={canJoin ? "noreferrer" : undefined}
                        disabled={!canJoin}
                        sx={{
                          bgcolor: "var(--primary-color)",
                          color: "#111",
                          fontWeight: 800,
                          borderRadius: 20,
                          px: 3,
                          "&:hover": { bgcolor: "var(--primary-hover-color)" },
                        }}
                      >
                        {event.finished ? labels[lang].finished
                          : event.status === "CANCELLED" ? labels[lang].cancelled
                          : canJoin ? labels[lang].join : labels[lang].accessSoon}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default EventsPage;
